import { WFeature, WKnownEntities, WPileEntity, WProcedure, WRelation, WStore, WUser } from "./index";
import traverse from "traverse";

function findByID(a: Array<any>, id: string) {
  return a.find(c => c.id == id);
}
function findByMap(a: any, id: string) {
  return a[id];
}


function inflateFeature(id: string, store: EnhancedWStore): WFeature {
  let feature = findByMap(store.maps.features, id);
  inflateFeatureFields(feature, store);
  return feature;
}

function inflateFeatureFields(feature: WFeature, store: EnhancedWStore) {
  if (feature) {
    // inflate children
    if (feature.childids && !feature.children)
      feature.children = feature.childids.map(id => findByMap(store.maps.features, id)).filter(id => id);
    if (feature.parentids && !feature.parents)
      feature.parents = feature.parentids.map(id => findByMap(store.maps.features, id)).filter(id => id);
  }
}

function inflateProcedure(id: string, store: WStore): WProcedure {
  return findByID(store.procedures, id);
}
function inflateRelation(id: string, store: EnhancedWStore): WRelation {
  return findByMap(store.maps.relations, id);
}
function inflateUser(id: string, store: WStore): WUser {
  return findByID(store.users, id);
}

function inflateCommitByPile(id: string, store: WStore): any {
  return store.commits
    && store.commits
      .filter(c => c.pileid == id)
      .sort((a, b) => a.commitDate - b.commitDate)
      .map(c => { c.user = c.userid && inflateUser(c.userid, store); return c });
}

function inflateCitations(id: string, store:WStore): any {
  return findByID(store.citations, id);
}

/**
 * find all references to IDs and replace them with references to their corresponding objects in teh store
 * @param object
 * @param store
 */
export function inflate(object: object, store: EnhancedWStore): any {

  traverse(object).forEach(function (o) {

    if (!o || typeof o !== "object")
      return;

    // this line avoids going down circular referneces... we only want to inflate
    // features once, from the root of the store. Not when we encounter them in the tree
    if (this.key == "feature" || this.key == "parents" || this.key == "children")
      this.update(o, true);

    if (o.featureid && !o.feature) {
      o.feature = inflateFeature(o.featureid, store);
    }
    if (o.procedureid && !o.procedure) {
      o.procedure = inflateProcedure(o.procedureid, store);
    }
    if (o.relationid && !o.relation) {
      o.relation = inflateRelation(o.relationid, store);
    }
    if (o.userid && !o.user) {
      o.user = inflateUser(o.userid, store);
    }
    if (o.pileid && !o.commits) {
      o.commits = inflateCommitByPile(o.pileid, store);
    }
    if (o.citationid && !o.citation) {
      o.citation = inflateCitations(o.citationid, store)
    }

  });
  return object;
}

type StoreMaps = {
  features?: { [id: string]: WFeature };
  relations?: { [id: string]: WRelation };
}
type EnhancedWStore = WStore & { maps?: StoreMaps };

export function inflateStore(store: WStore): EnhancedWStore {
  let ret: EnhancedWStore = store;
  if (store) {
    // add maps to store
    let features = store.features ? mapByID(store.features) : {};
    let relations = store.relations ? mapByID(store.relations) : {};
    ret.maps = { features, relations };

    // fix features
    if (store.features) {
      ret.features = inflate(store.features, ret);
      store.features.forEach(f => inflateFeatureFields(f, ret));
    }
    // fix relations
    if (store.relations) {
      ret.relations = inflate(store.relations, ret);
    }

    // fix rule values within store
    ret.ruleValues = inflate(store.ruleValues, ret);
    // fix known features
    ret.knownEntities = inflate(store.knownEntities, ret);

    // save known entities in a well known place
    // todo - we need to improve this!!!
    WKnownEntities.entities = store.knownEntities;

  }
  return ret;
}


function mapByID<T extends WPileEntity>(arr: Array<T>): { [id: string]: T } {
  return arr.reduce((map, elem) => (map[elem.id] = elem) && map, {});
}


/**
 * replace objects with their id to comply with server protocol
 * @param object
 */

export function deflate(object) {
  return traverse(object).map(o => {

    if (!o || typeof o !== "object")
      return;

    if (o.hasOwnProperty("feature")) {
      o.featureid = o.feature && o.feature.id;
      delete o.feature; // todo improve, i need to delete null features because the java server is too picky
    }

    if (o.hasOwnProperty("relation")) {
      o.relationid = o.relation && o.relation.id;
      delete o.relation;
    }

    if (o.hasOwnProperty("procedure")) {
      o.procedureid = o.procedure && o.procedure.id;
      delete o.procedure;
    }

    if (o.hasOwnProperty("citation")) {
      o.citationid = o.citation && o.citation.id;
      delete o.citation;
    }
    if (o.hasOwnProperty("patientFacingQualifier")) {
      o.patientFacingQualifierid = o.patientFacingQualifier && o.patientFacingQualifier.id;
      delete o.patientFacingQualifier;
    }

    if (o["children"] && Array.isArray(o["children"])) {
      delete o.children;
    }
    if (o["childids"] && Array.isArray(o["childids"])) {
      delete o.childids;
    }
    if (o["parents"] && Array.isArray(o["parents"])) {
      o["parentids"] = o.parents.map(p => p.id);
      delete o.parents;
    }
    if (o["panels"] && Array.isArray(o["panels"])) {
      delete o.panels;
    }
    if (o["attributes"] && Array.isArray(o["attributes"])) {
      delete o.attributes;
    }
    if (o["procedures"] && Array.isArray(o["procedures"])) {
      delete o.procedures;
    }
    if (o["diagnoseDisorders"] && Array.isArray(o["diagnoseDisorders"])) {
      delete o.diagnoseDisorders;
    }
    if (o["diagnoseTests"] && Array.isArray(o["diagnoseTests"])) {
      delete o.diagnoseTests;
    }
    if (o["panelsToConsiders"] && Array.isArray(o["panelsToConsiders"])) {
      delete o.panelsToConsiders;
    }
    if (o["ancestors"] && Array.isArray(o["ancestors"])) {
      delete o.ancestors;
    }
    if (o["ancestorids"] && Array.isArray(o["ancestorids"])) {
      delete o.ancestorids;
    }
    if (o["inferredQualiifers"] && Array.isArray(o["inferredQualiifers"])) {
      delete o.inferredQualiifers;
    }
    if (o["belongsToPanels"] && Array.isArray(o["belongsToPanels"])) {
      delete o.belongsToPanels;
    }
    if (o["discussions"] && Array.isArray(o["discussions"])) {
      delete o.discussions;
    }

    delete o.commitsHistory;
    delete o.user;
    delete o.commits;
    delete o.citaions;
    delete o.cancidateDefinedAs;
  });
}



